import { PositionType } from 'domain/shared/employee';
import { RuleValidationResult, TargetRule } from '../types';
import type { Technology } from 'domain/shared/career';

const SOFTWARE_ENGINEER = 'Software Engineer';

export const techSpecializations = [SOFTWARE_ENGINEER];

export const isMoreSpecific = (specificRule: TargetRule, generalRule: TargetRule): boolean => {
  if (specificRule.competencyLevel !== generalRule.competencyLevel && generalRule.competencyLevel !== '') {
    return false;
  }
  if (specificRule.positionId !== generalRule.positionId && generalRule.positionId) {
    return false;
  }
  if (specificRule.technologyId !== generalRule.technologyId && generalRule.technologyId) {
    return false;
  }

  const isMoreSpecificLevel = specificRule.competencyLevel !== '' && generalRule.competencyLevel === '';
  const isMoreSpecificPosition = specificRule.positionId && !generalRule.positionId;
  const isMoreSpecificTech = specificRule.technologyId !== '' && generalRule.technologyId === '';

  return isMoreSpecificLevel || isMoreSpecificPosition || isMoreSpecificTech;
};

export const isLevelOnlyRule = (rule: Omit<TargetRule, 'id'>): boolean => {
  return rule.competencyLevel !== '' && !rule.positionId && rule.technologyId === '';
};

export const getRuleDescription = (
  competencyLevel: string | undefined,
  position: PositionType | undefined,
  technology: Technology | undefined,
) => {
  const parts: string[] = [];

  if (competencyLevel && competencyLevel !== '') parts.push(competencyLevel);
  if (position) parts.push(position.name);
  if (technology) parts.push(technology.name);

  if (parts.length === 0) return 'any positions';
  return parts.join(' ');
};

export const calculateAllocatedCount = (ruleList: TargetRule[]): number => {
  const topLevelRules = ruleList.filter(rule => {
    return !ruleList.some(otherRule => otherRule.id !== rule.id && isMoreSpecific(rule, otherRule));
  });

  return topLevelRules.reduce((sum, rule) => sum + rule.capacity, 0);
};

export const validateRule = (
  rules: TargetRule[],
  totalHeadcount: number,
  positions: PositionType[],
  newRule: Omit<TargetRule, 'id'>,
  technologies: Technology[],
  editingId?: string,
): RuleValidationResult => {
  const allocatedCount = calculateAllocatedCount(rules);
  const remainingCount = totalHeadcount - allocatedCount;

  const ruleCount = newRule.capacity || 0;

  if (isLevelOnlyRule(newRule)) {
    const existingLevelOnlyRule = rules.find(
      rule => rule.id !== editingId && isLevelOnlyRule(rule) && rule.competencyLevel === newRule.competencyLevel,
    );

    if (existingLevelOnlyRule) {
      return {
        valid: false,
        message: `A level-only rule for ${newRule.competencyLevel} already exists`,
        availableCount: 0,
      };
    }
  }

  const parentRules = rules.filter(rule => {
    if (rule.id === editingId) return false;
    return isMoreSpecific({ ...newRule, id: '' } as TargetRule, rule);
  });

  if (parentRules.length > 0) {
    const parentAvailabilities = parentRules.map(parentRule => {
      const siblingRules = rules.filter(
        r => r.id !== parentRule.id && r.id !== editingId && isMoreSpecific(r, parentRule),
      );

      const siblingCount = siblingRules.reduce((sum, r) => sum + r.capacity, 0);
      const availableCount = parentRule.capacity - siblingCount;

      return {
        parentRule,
        availableCount,
      };
    });

    const mostRestrictiveParent = parentAvailabilities.reduce(
      (min, current) => (current.availableCount < min.availableCount ? current : min),
      parentAvailabilities[0],
    );

    const minAvailableCount = mostRestrictiveParent.availableCount;
    const mostRestrictiveParentRule = mostRestrictiveParent.parentRule;

    if (minAvailableCount <= 0) {
      const position = positions?.find((element: PositionType) => element.id === mostRestrictiveParentRule.positionId);
      const technology = technologies?.find(
        (element: Technology) => element.id === mostRestrictiveParentRule.technologyId,
      );
      return {
        valid: false,
        message: `No space available in parent: ${getRuleDescription(mostRestrictiveParentRule.competencyLevel, position, technology)}`,
        availableCount: 0,
      };
    }

    if (ruleCount > minAvailableCount) {
      return {
        valid: false,
        message: `Only ${minAvailableCount} available in parent`,
        availableCount: minAvailableCount,
      };
    }

    return { valid: true, availableCount: minAvailableCount };
  }

  if (editingId) {
    const editingRule = rules.find(r => r.id === editingId);
    if (editingRule) {
      const isTopLevel = !rules.some(r => r.id !== editingId && isMoreSpecific(editingRule, r));

      if (isTopLevel) {
        const adjustedRemainingCount = remainingCount + editingRule.capacity;

        if (ruleCount > adjustedRemainingCount) {
          return {
            valid: false,
            message: `Exceeds available headcount by ${ruleCount - adjustedRemainingCount}`,
            availableCount: adjustedRemainingCount,
          };
        }

        return { valid: true, availableCount: adjustedRemainingCount };
      }
    }
  }

  if (ruleCount > remainingCount) {
    return {
      valid: false,
      message: `Exceeds remaining headcount by ${ruleCount - remainingCount}`,
      availableCount: remainingCount,
    };
  }

  return { valid: true, availableCount: remainingCount };
};
